/* eslint-disable max-len */
import React, { useState } from 'react';
import { Form, Radio, Space, Upload } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { toUpper } from 'lodash';
import PropTypes from 'prop-types';

import InputComponent from '@/components/Input';
import SelectInput from '@/components/Select';
import ButtonComponent from '@/components/Button';
import TextAreaComponent from '@/components/TextArea';
import { MAX_FILE_SIZE, SENDER_CONSTANT } from '@/constants';
import { isValidHttpUrl } from '@/utils/validate';
import { notificationUtil } from '@/utils';
import { ReactComponent as SentIcon } from '@/assets/images/icons/send.svg';
import { ReactComponent as FileUploadIcon } from '@/assets/images/icons/file-upload.svg';

import './index.scss';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

function NewSenderNameForm({ onSubmit }) {
  const [formInstance] = Form.useForm();

  const [evidenceValue, setEvidenceValue] = useState();
  const { t } = useTranslation();
  const onChangeCompanyInfo = (e) => {
    setEvidenceValue(e.target.value);
    formInstance.resetFields(['registrationDocument', 'websiteLink']);
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.file?.originFileObj;
  };

  const handleCustomRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const handleNormalizeSenderName = (value) =>
    toUpper(value.replace(/\s/g, ''));

  const fileTypeAllowed = ['application/pdf', 'image/png', 'image/jpeg'];
  const handleBeforeUpload = (file) => {
    const isValidFile = fileTypeAllowed.includes(file.type);
    if (!isValidFile) {
      notificationUtil.setErrorMessage({
        title: t('sms_create_new_request.upload_file'),
        description: t('sms_create_new_request.invalid_file'),
      });
    }

    const isLtMFS = file.size / 1024 / 1024 < MAX_FILE_SIZE;

    if (!isLtMFS) {
      notificationUtil.setErrorMessage({
        title: t('sms_create_new_request.upload_file'),
        description: `File must smaller than ${MAX_FILE_SIZE}MB!`,
      });
    }
    return (isValidFile && isLtMFS) || Upload.LIST_IGNORE;
  };

  return (
    <div className="new-sender-name-form-container">
      <Form
        layout="vertical"
        onFinish={onSubmit}
        autoComplete="off"
        className="form"
        form={formInstance}
      >
        <Form.Item
          label={t('sms_add_new_sender.sender_name')}
          name="senderName"
          className="form-item"
          tooltip={{
            title: (
              <>
                <p>{t('sms_add_new_sender.error_code.note_characters')}</p>
                <p>{t('sms_add_new_sender.error_code.character_names')}</p>
              </>
            ),
            placement: 'bottom',
            icon: <InfoCircleOutlined />,
          }}
          rules={[
            {
              required: true,
              message: t('sms_add_new_sender.error_code.sendername.required'),
            },
            {
              max: 11,
              message: t(
                'sms_add_new_sender.error_code.sendername.character_names',
              ),
            },
          ]}
          normalize={handleNormalizeSenderName}
        >
          <InputComponent
            placeholder={t('sms_add_new_sender.placeholder.sendername')}
          />
        </Form.Item>

        <Form.Item
          label={t('sms_add_new_sender.type_of_use')}
          name="useType"
          className="form-item"
          rules={[
            {
              required: true,
              message: t('sms_add_new_sender.error_code.type_of_use.required'),
            },
          ]}
        >
          <SelectInput
            allowClear
            placeholder={t('sms_add_new_sender.type_of_use')}
            options={SENDER_CONSTANT.USE_TYPES}
          />
        </Form.Item>

        <Form.Item
          label={t('sms_add_new_sender.company_name')}
          name="companyName"
          className="form-item"
          rules={[
            {
              required: true,
              message: t('sms_add_new_sender.error_code.company_name.required'),
            },
          ]}
        >
          <InputComponent
            placeholder={t('sms_add_new_sender.placeholder.company_name')}
          />
        </Form.Item>

        <Form.Item
          label={
            <span className="fw-500">
              {t('sms_add_new_sender.select_evidence.title')}
            </span>
          }
          name="evidence"
          rules={[
            {
              required: true,
              message: t(
                'sms_add_new_sender.error_code.select_evidence.required',
              ),
            },
          ]}
        >
          <Radio.Group onChange={onChangeCompanyInfo} value={evidenceValue}>
            <Space direction="vertical">
              <Radio value="companyRegistrationDocument">
                {t('sms_add_new_sender.select_evidence.select_1')}
              </Radio>
              {evidenceValue === 'companyRegistrationDocument' && (
                <Form.Item
                  name="registrationDocument"
                  valuePropName="file"
                  rules={[
                    {
                      required: evidenceValue === 'companyRegistrationDocument',
                      message: t(
                        'sms_add_new_sender.error_code.upload_your_file.required',
                      ),
                    },
                  ]}
                  getValueFromEvent={normFile}
                >
                  <Dragger
                    className="upload-file"
                    multiple={false}
                    accept=".pdf, .png, .jpg"
                    customRequest={handleCustomRequest}
                    maxCount={1}
                    beforeUpload={handleBeforeUpload}
                  >
                    <p className="ant-upload-drag-icon">
                      <FileUploadIcon />
                    </p>
                    <p className="ant-upload-text">
                      {t('sms_add_new_sender.upload_image')}
                    </p>
                    <div className="ant-upload-hint">
                      <p className="support-files">
                        {t('sms_add_new_sender.only_file_upload')}
                      </p>
                    </div>
                  </Dragger>
                </Form.Item>
              )}

              <Radio value="companyWebsite">
                {t('sms_add_new_sender.select_evidence.select_2')}
              </Radio>
              {evidenceValue === 'companyWebsite' && (
                <Form.Item
                  name="websiteLink"
                  rules={[
                    {
                      required: evidenceValue === 'companyWebsite',
                      message: t(
                        'sms_add_new_sender.error_code.company_website.required',
                      ),
                    },
                    () => ({
                      validator(_, value) {
                        if (value && !isValidHttpUrl(value)) {
                          return Promise.reject(
                            new Error(
                              t(
                                'sms_add_new_sender.error_code.input_valid.required',
                              ),
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputComponent
                    placeholder={t(
                      'sms_add_new_sender.placeholder.company_website',
                    )}
                    type="url"
                  />
                </Form.Item>
              )}
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={t('sms_add_new_sender.sms_use_cases')}
          name="description"
          rules={[
            {
              required: true,
              message: t(
                'sms_add_new_sender.error_code.sms_use_cases.required',
              ),
            },
          ]}
        >
          <TextAreaComponent
            placeholder={t('sms_add_new_sender.placeholder.sms_use_cases')}
            rows={5}
          />
        </Form.Item>

        <Form.Item>
          <div className="form-actions-item">
            <ButtonComponent
              type="primary"
              htmlType="submit"
              icon={<SentIcon />}
            >
              {t('button.create')}
            </ButtonComponent>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}

NewSenderNameForm.propTypes = {
  onSubmit: PropTypes.func,
};

export default NewSenderNameForm;
